import { brand } from '@alison/ui';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { type MarketScope, useMarketPerformance } from '../data/api';
import type { MarketRow } from '../data/marketPerformanceData';
import type { MarketCoverage } from '../data/types';
import { coverageNote, filterByCountries, resolveMarket } from '../data/utils';
import { ErrorState } from '../shared/ErrorState';
import { NoData } from '../shared/NoData';
import { MarketBars } from './MarketBars';
import { MarketTiles } from './MarketTiles';
import { type MarketView, MarketViewToggle } from './MarketViewToggle';
import { SpendVsSavings } from './SpendVsSavings';
import { TopMarketsControl, type TopMarketsSelection } from './TopMarketsControl';

interface MarketPerformanceCardProps {
  market: MarketRow | null;
  markets: MarketRow[];
  currencySymbol: string;
  coverage: MarketCoverage | null;
  topMarkets: TopMarketsSelection;
  isLoading: boolean;
  view: MarketView;
  onViewChange: (value: MarketView) => void;
  rankingMarkets?: MarketRow[];
  rankOffset?: number;
  allowPrintSplit?: boolean;
}

function CardHeader({
  market,
  view,
  onViewChange,
  coverage,
  topMarkets,
}: {
  market: MarketRow | null;
  view: MarketView;
  onViewChange: (value: MarketView) => void;
  coverage: MarketCoverage | null;
  topMarkets: TopMarketsSelection;
}): ReactNode {
  const isAll = market === null;
  const note = isAll ? coverageNote(coverage) : null;
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 3,
        flexWrap: 'wrap',
        py: 4,
        px: 5,
        background: `linear-gradient(110deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.page} 100%)`,
        borderBottom: '1px solid',
        borderColor: 'divider',
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BarChartOutlined sx={{ fontSize: 16, color: brand.purple }} />
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            {isAll ? 'Market Performance' : `${market.market} Performance`}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
          {isAll
            ? 'Ranked by performance · Lower potential savings = Less wasted spend'
            : 'Single-market view. Switch Market to All to compare'}
        </Typography>
        {note ? (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{note}</Typography>
        ) : null}
      </Box>

      {isAll ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            '@media print': { display: 'none' },
          }}
        >
          <TopMarketsControl {...topMarkets} />
          <MarketViewToggle value={view} onChange={onViewChange} />
        </Box>
      ) : null}
    </Box>
  );
}

function MarketPerformanceCard({
  market,
  markets,
  currencySymbol,
  coverage,
  topMarkets,
  isLoading,
  view,
  onViewChange,
  rankingMarkets,
  rankOffset,
  allowPrintSplit = false,
}: MarketPerformanceCardProps): ReactNode {
  return (
    <Box
      sx={(theme) => ({
        mb: 4,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: theme.palette.border.default,
        borderRadius: `${theme.radius.xl}px`,
        boxShadow: theme.shadows[5],
        '@media print': {
          overflow: 'visible',
          breakInside: allowPrintSplit ? 'auto' : 'avoid',
        },
      })}
    >
      <CardHeader
        market={market}
        view={view}
        onViewChange={onViewChange}
        coverage={coverage}
        topMarkets={topMarkets}
      />
      <Box sx={{ pt: 4, px: 5, pb: 5 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : market ? (
          <MarketTiles market={market} currencySymbol={currencySymbol} />
        ) : markets.length === 0 ? (
          <NoData />
        ) : view === 'spend' ? (
          <SpendVsSavings markets={markets} currencySymbol={currencySymbol} />
        ) : (
          <MarketBars
            markets={markets}
            {...(rankingMarkets ? { rankingMarkets } : {})}
            {...(rankOffset === undefined ? {} : { rankOffset })}
            currencySymbol={currencySymbol}
          />
        )}
      </Box>
    </Box>
  );
}

export function MarketPerformance({
  selectedCountry,
  selectedCountries,
  scope,
  enabled,
  topMarkets,
}: {
  selectedCountry: string | null;
  selectedCountries: string[];
  scope: MarketScope;
  enabled: boolean;
  topMarkets: TopMarketsSelection;
}): ReactNode {
  const [view, setView] = useState<MarketView>('performance');
  const { data, currencySymbol, coverage, isLoading, isError } = useMarketPerformance(
    scope,
    enabled,
  );
  const { markets } = data;
  const market = selectedCountry ? resolveMarket(selectedCountry, markets) : null;
  const visibleMarkets = filterByCountries(markets, selectedCountries);
  // A market filter narrows the rows client-side, so the top-N coverage no longer describes them.
  const shownCoverage = selectedCountries.length === 0 ? coverage : null;
  const firstPerformancePageSize = visibleMarkets.length > 7 ? 7 : visibleMarkets.length;
  const remainingPerformanceMarkets = visibleMarkets.slice(firstPerformancePageSize);
  const remainingPerformancePageCount = Math.ceil(remainingPerformanceMarkets.length / 12);
  const remainingPerformancePageSize = Math.ceil(
    remainingPerformanceMarkets.length / Math.max(1, remainingPerformancePageCount),
  );
  const performancePages = [
    visibleMarkets.slice(0, firstPerformancePageSize),
    ...Array.from({ length: remainingPerformancePageCount }, (_, page) =>
      remainingPerformanceMarkets.slice(
        page * remainingPerformancePageSize,
        page * remainingPerformancePageSize + remainingPerformancePageSize,
      ),
    ),
  ].filter((page) => page.length > 0);
  const spendPages = Array.from({ length: Math.ceil(visibleMarkets.length / 12) }, (_, page) =>
    visibleMarkets.slice(page * 12, page * 12 + 12),
  );

  if (isError) return <ErrorState />;

  return (
    <>
      <Box sx={{ '@media print': { display: 'none' } }}>
        <MarketPerformanceCard
          market={market}
          markets={visibleMarkets}
          currencySymbol={currencySymbol}
          coverage={shownCoverage}
          topMarkets={topMarkets}
          isLoading={isLoading}
          view={view}
          onViewChange={setView}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: '100%',
          height: 0,
          overflow: 'hidden',
          '@media print': {
            position: 'static',
            visibility: 'visible',
            pointerEvents: 'auto',
            height: 'auto',
            overflow: 'visible',
            breakAfter: 'page',
          },
        }}
      >
        {market ? (
          <MarketPerformanceCard
            market={market}
            markets={visibleMarkets}
            currencySymbol={currencySymbol}
            coverage={shownCoverage}
            topMarkets={topMarkets}
            isLoading={isLoading}
            view="performance"
            onViewChange={setView}
          />
        ) : (
          performancePages.map((pageMarkets, page) => (
            <Box
              key={`performance-${pageMarkets[0]?.code}-${pageMarkets.at(-1)?.code}`}
              sx={page === 0 ? undefined : { breakBefore: 'page' }}
            >
              <MarketPerformanceCard
                market={null}
                markets={pageMarkets}
                rankingMarkets={visibleMarkets}
                rankOffset={performancePages
                  .slice(0, page)
                  .reduce((offset, previousPage) => offset + previousPage.length, 0)}
                currencySymbol={currencySymbol}
                coverage={shownCoverage}
                topMarkets={topMarkets}
                isLoading={isLoading}
                view="performance"
                onViewChange={setView}
                allowPrintSplit={page === 0}
              />
            </Box>
          ))
        )}
        {market === null
          ? spendPages.map((pageMarkets) => (
              <Box
                key={`spend-${pageMarkets[0]?.code}-${pageMarkets.at(-1)?.code}`}
                sx={{ breakBefore: 'page' }}
              >
                <MarketPerformanceCard
                  market={null}
                  markets={pageMarkets}
                  currencySymbol={currencySymbol}
                  coverage={shownCoverage}
                  topMarkets={topMarkets}
                  isLoading={isLoading}
                  view="spend"
                  onViewChange={setView}
                />
              </Box>
            ))
          : null}
      </Box>
    </>
  );
}
